// ============================================
// ORDER TIME UTILITY FUNCTIONS
// Shared utilities for grace period and auto-complete logic
// ============================================

// ============================================
// CONFIGURABLE GRACE PERIODS (in milliseconds)
// ============================================
// Time after pickup before buyer can confirm receipt (default: 1 hour)
export const CONFIRM_GRACE_PERIOD_MS = (1/6) * 60 * 60 * 1000; // 10 minutes

// Time after pickup before auto-completing if buyer doesn't respond (default: 1 day)
export const AUTO_COMPLETE_PERIOD_MS = 1 * 24 * 60 * 60 * 1000; // 1 day

/**
 * Check if the grace period has passed (buyer can now confirm receipt)
 * @param {string} pickupTimeStr - The scheduled pickup time
 * @returns {boolean} - True if grace period has passed
 */
export const canConfirmReceipt = (pickupTimeStr) => {
  if (!pickupTimeStr || pickupTimeStr === 'Not specified') return false;
  try {
    const pickupTime = new Date(pickupTimeStr);
    if (isNaN(pickupTime.getTime())) return false;
    const graceEndTime = new Date(pickupTime.getTime() + CONFIRM_GRACE_PERIOD_MS);
    return new Date() >= graceEndTime;
  } catch {
    return false;
  }
};

/**
 * Check if buyer can confirm receipt immediately.
 * Allows confirmation immediately if vendor has set the item to 'ready_for_pickup'
 * and the scheduled pickup time has been reached, regardless of the grace period.
 * Falls back to the regular grace-period check otherwise.
 * @param {string} status - Current order item status
 * @param {string} pickupTimeStr - The scheduled pickup time
 * @returns {boolean} - True if buyer may confirm now
 */
export const canConfirmReceiptNow = (status, pickupTimeStr) => {
  if (!pickupTimeStr || pickupTimeStr === 'Not specified') return false;
  try {
    const pickupTime = new Date(pickupTimeStr);
    if (isNaN(pickupTime.getTime())) return false;

    const now = new Date();

    // If vendor marked as ready for pickup and pickup time reached, allow immediate confirmation
    if (status === 'ready_for_pickup' && now >= pickupTime) return true;

    // Otherwise fall back to grace period logic
    const graceEndTime = new Date(pickupTime.getTime() + CONFIRM_GRACE_PERIOD_MS);
    return now >= graceEndTime;
  } catch {
    return false;
  }
};

/**
 * Check if auto-complete period has passed (should auto-mark as completed)
 * @param {string} pickupTimeStr - The scheduled pickup time
 * @returns {boolean} - True if auto-complete period has passed
 */
export const shouldAutoComplete = (pickupTimeStr) => {
  if (!pickupTimeStr || pickupTimeStr === 'Not specified') return false;
  try {
    const pickupTime = new Date(pickupTimeStr);
    if (isNaN(pickupTime.getTime())) return false;
    const autoCompleteTime = new Date(pickupTime.getTime() + AUTO_COMPLETE_PERIOD_MS);
    return new Date() >= autoCompleteTime;
  } catch {
    return false;
  }
};

/**
 * Get time remaining until buyer can confirm receipt
 * @param {string} pickupTimeStr - The scheduled pickup time
 * @returns {string} - Human-readable time remaining
 */
export const getTimeUntilCanConfirm = (pickupTimeStr) => {
  if (!pickupTimeStr || pickupTimeStr === 'Not specified') return '';
  try {
    const pickupTime = new Date(pickupTimeStr);
    if (isNaN(pickupTime.getTime())) return '';
    const graceEndTime = new Date(pickupTime.getTime() + CONFIRM_GRACE_PERIOD_MS);
    const now = new Date();
    const diff = graceEndTime - now;
    if (diff <= 0) return '';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  } catch {
    return '';
  }
};

/**
 * Get time remaining until auto-complete
 * @param {string} pickupTimeStr - The scheduled pickup time
 * @returns {string} - Human-readable time remaining
 */
export const getTimeUntilAutoComplete = (pickupTimeStr) => {
  if (!pickupTimeStr || pickupTimeStr === 'Not specified') return '';
  try {
    const pickupTime = new Date(pickupTimeStr);
    if (isNaN(pickupTime.getTime())) return '';
    const autoCompleteTime = new Date(pickupTime.getTime() + AUTO_COMPLETE_PERIOD_MS);
    const now = new Date();
    const diff = autoCompleteTime - now;
    if (diff <= 0) return '';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  } catch {
    return '';
  }
};

/**
 * Check if an order item should show confirm receipt section
 * Based on pickup time and current status
 * @param {string} status - Current order item status
 * @param {string} pickupTimeStr - The scheduled pickup time
 * @returns {boolean} - True if confirm section should be shown
 */
export const shouldShowConfirmReceipt = (status, pickupTimeStr) => {
  // Don't show if already completed or marked as not completed
  if (status === 'completed' || status === 'not_completed') {
    return false;
  }
  
  // Show if pickup time has passed (regardless of status)
  if (!pickupTimeStr || pickupTimeStr === 'Not specified') return false;
  try {
    const pickupTime = new Date(pickupTimeStr);
    if (isNaN(pickupTime.getTime())) return false;
    return new Date() >= pickupTime;
  } catch {
    return false;
  }
};
